import { FC, useState, useMemo, cloneElement, ChangeEvent, ReactElement, MouseEvent } from 'react';
import {
  Avatar as _Avatar,
  Dialog,
  Drawer,
  Grid,
  LinearProgress,
  MenuList,
  MenuItem,
  Popover,
  Typography,
  TextField,
} from '@material-ui/core';
import BusinessIcon from '@material-ui/icons/Business';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import InsertDriveFileOutlinedIcon from '@material-ui/icons/InsertDriveFileOutlined';
import _MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import _CreateIcon from '@material-ui/icons/Create';
import _DeleteIcon from '@material-ui/icons/Delete';
import styled from '@emotion/styled';
import { useQuery, useMutation } from '@apollo/client';

import globalState from '../../../../operations/commands';
import {
  // DeleteAttachablesAttachmentsDocument,
  // DeleteAttachablesAttachmentsMutation,
  // DeleteAttachablesAttachmentsMutationVariables,
  DeleteCommentDocument,
  DeleteCommentMutation,
  DeleteCommentMutationVariables,
  GetCommentsDocument,
  GetCommentsQuery,
  GetCommentsQueryVariables,
  PostCommentDocument,
  PostCommentMutation,
  PostCommentMutationVariables,
  PostAttachmentDocument,
  PostAttachmentMutation,
  PostAttachmentMutationVariables,
  PutCommentDocument,
  PutCommentMutation,
  PutCommentMutationVariables,
  Variation,
  Comment,
  AttachablesAttachment,
  Attachment,
} from '../../../../generated/graphql';
import { PaperContainer } from '../../primitive/Paper';
import { SPACING_SMALL } from '../../../constant/spacing';
import { COLOR_TEXT_MAIN, COLOR_RED } from '../../../../theme/colors';
import { DrawerContent, PrimaryButton, SecondaryButton } from './common';
import { Container as DrawerContainer } from '../../compound/Drawer';
import { ModalContent, ModalFooter, ModalTitle } from '../../primitive/Modal';
import {
  SectionTitle as _SectionTitle,
  BodyBold as _BodyBold,
  BodySmall,
  LabelExtraSmallStyle,
  Link as _Link,
} from '../../primitive/TypographyUI';
import { convertJST } from '../../../../utility/convertJST';
import mutationVariables from '../../../../utility/mutationVariables';
import useFileUpload from '../../../../hooks/useFileUpload';

type Props = {
  variation: Variation;
  variationRefetch: () => void;
};

const Comments: FC<Props> = ({ variation, variationRefetch }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [popoverAnchor, setPopoverAnchor] = useState<{ index: number; anchor: SVGSVGElement } | null>(null);
  const handlePopoverOpen = (event: MouseEvent<SVGSVGElement>, index: number) => {
    setPopoverAnchor({ index: index, anchor: event.currentTarget });
  };
  const handlePopoverClose = () => {
    setPopoverAnchor(null);
  };
  const [isCommentEditDrawerOpen, setIsCommentEditDrawerOpent] = useState(false);
  const [isDeleteCommenModalOpen, setIsDeleteCommentModalOpen] = useState(false);
  const toggleCommentEdit = () => setIsCommentEditDrawerOpent((prev) => !prev);
  const toggleCommentDelete = () => setIsDeleteCommentModalOpen((prev) => !prev);

  const {
    data: { comments } = { versions: null },
    loading: commentsLoading,
    refetch: commmentsRefetch,
  } = useQuery<GetCommentsQuery, GetCommentsQueryVariables>(GetCommentsDocument, {
    variables: { variation_id: variation.id },
    context: { clientName: 'planbcd' },
  });

  const [postComment] = useMutation<PostCommentMutation, PostCommentMutationVariables>(PostCommentDocument, {
    context: { clientName: 'planbcd' },
  });

  const [postAttachements] = useMutation<PostAttachmentMutation, PostAttachmentMutationVariables>(
    PostAttachmentDocument,
    { context: { clientName: 'planbcd' } },
  );

  const refetch = () => {
    commmentsRefetch();
    variationRefetch();
  };

  const onClickSubmit = (newComment: string, files: File[]) => {
    if (!newComment) {
      return globalState.setErrors({
        message: 'コメントを入力してください',
        name: 'NonFatalError',
        context: 'planbcd',
      });
    }
    setIsProcessing(true);
    postComment(
      mutationVariables({
        id: variation.id,
        variation_comment: {
          with_action: 'create_edit_request',
          comment: newComment,
        },
      }),
    )
      .then(({ data }) => {
        if (files.length) {
          const { postComment: postCommentData } = data as PostCommentMutation;
          Promise.all(
            files.map((file) => {
              return postAttachements(
                mutationVariables({
                  id: postCommentData.id,
                  data: file,
                }),
              );
            }),
          ).finally(() => {
            refetch();
            setIsProcessing(false);
          });
        } else {
          console.log('post omment cusss', data);
          refetch();
          setIsProcessing(false);
        }
      })
      .catch((error) => {
        globalState.setErrors({
          message: `コメントの送信ができませんでした: (${error.message})`,
          name: 'NonFatalError',
          context: 'planbcd',
        });
        setIsProcessing(false);
      });
  };

  const hasCommentsToShow = (comments && comments.length) || variation.permissions?.show_comments?.permit;

  if (commentsLoading) return <LinearProgress />;
  return (
    <>
      <PaperContainer>
        <SectionTitle>コメント一覧</SectionTitle>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <BodySmall>本デザイン案について、制作担当者とカスターマー間の連絡用にご利用ください。</BodySmall>
            <BodySmall>デザイン制作素材や資料などのやりとりにもご利用いただけます。</BodySmall>
          </Grid>
        </Grid>
        <CommentEditor
          callbackForOnClickPrimary={onClickSubmit}
          _PrimaryAction={<PrimaryButton>コメントを送信する</PrimaryButton>}
        />
        {isProcessing && <LinearProgress />}
      </PaperContainer>
      <ColumnFlexWrapper>
        {hasCommentsToShow &&
          comments!.map((comment, i) => (
            <PaperContainer key={i}>
              <Grid container alignItems="center">
                <Grid item>
                  {comment?.user?.avatar_image_url ? (
                    <Avatar src={comment?.user?.avatar_image_url} />
                  ) : variation?.plan_a ? (
                    <Avatar>
                      <BusinessIcon />
                    </Avatar>
                  ) : (
                    <Avatar>
                      <AccountCircleIcon />
                    </Avatar>
                  )}
                </Grid>
                <NameAndDate>
                  <Link to={comment?.user.href}>{comment?.user?.username || ''}</Link>
                  {convertJST(comment.updated_at || comment.created_at)}
                </NameAndDate>
                {comment?.permissions?.update?.permit && (
                  <>
                    <MoreHorizIcon onClick={(e) => handlePopoverOpen(e, i)} />
                    <Popover
                      id="mouse-over-popover"
                      open={popoverAnchor?.index === i}
                      anchorEl={popoverAnchor?.anchor}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                      }}
                      onClose={handlePopoverClose}
                      disableRestoreFocus
                    >
                      <MenuList>
                        <MenuItem onClick={toggleCommentEdit}>
                          <CreateIcon />
                          <span>編集</span>
                        </MenuItem>
                        {comment?.permissions?.destroy?.permit && (
                          <MenuItem onClick={toggleCommentDelete}>
                            <DeleteIcon />
                            <span>削除</span>
                          </MenuItem>
                        )}
                      </MenuList>
                      <EditCommentDrawer
                        comment={comment}
                        refetch={refetch}
                        isOpen={isCommentEditDrawerOpen}
                        onClose={toggleCommentEdit}
                      />
                      <DeleteCommentModal
                        isOpen={isDeleteCommenModalOpen}
                        commentId={comment.id}
                        refetch={refetch}
                        onClose={() => {
                          toggleCommentDelete();
                          handlePopoverClose();
                        }}
                      />
                    </Popover>
                  </>
                )}
              </Grid>
              <Grid container alignItems="center">
                <EmptyFlexItem item />
                <BodySmall>{comment.comment}</BodySmall>
              </Grid>
              {comment.attachables_attachments?.map((attachable: AttachablesAttachment, i: number) => (
                <AttachmentRow key={`attachments_${i}`}>
                  <ImageLink to={attachable.attachment.urls.original} target="_blank" rel="noreferrer">
                    {attachable.attachment.content_type.indexOf('image/') === 0 ? (
                      <UploadedImage src={attachable.attachment.urls.original} />
                    ) : (
                      <FileOutlinedIcon fontSize="large" />
                    )}
                  </ImageLink>
                  <Link
                    to={attachable.attachment.urls.original}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: '#006BD6', textDecoration: 'none' }}
                  >
                    {attachable.attachment.name}
                  </Link>
                </AttachmentRow>
              ))}
            </PaperContainer>
          ))}
      </ColumnFlexWrapper>
    </>
  );
};

const CommentEditor: FC<{
  callbackForOnClickPrimary: (newComment: string, files: File[]) => void;
  initialComment?: string;
  initialFiles?: Attachment[];
  _PrimaryAction?: ReactElement;
  SecondaryAction?: ReactElement;
}> = ({ callbackForOnClickPrimary, initialFiles, _PrimaryAction, SecondaryAction, initialComment }) => {
  const { files, onDrop, removeFile } = useFileUpload();
  const [newComment, setNewComment] = useState(initialComment ?? '');
  const [wordCount, setWordCount] = useState(0);

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const str = e.target.value.substring(0, 4000);
    setNewComment(str);
    setWordCount(str.length);
  };

  const onClickPrimary = () => {
    callbackForOnClickPrimary && callbackForOnClickPrimary(newComment, files);
  };

  const PrimaryAction =
    _PrimaryAction &&
    cloneElement(_PrimaryAction, {
      disabled: !newComment || wordCount === 0,
      onClick: onClickPrimary,
    });

  const SelectedImages = useMemo(
    () => [
      ...(initialFiles
        ? initialFiles.map((attachment, i) => (
            <Grid key={`uploaded-image-${i}`} container alignItems="center">
              {attachment.content_type.indexOf('image/') !== 0 ? (
                <InsertDriveFileOutlinedIcon fontSize="large" />
              ) : (
                <UploadedImage src={attachment.urls.original} />
              )}
              <span>{attachment.name}</span>
            </Grid>
          ))
        : []),
      ...files.map((file, i) => (
        <Grid key={`selecetd-image-${i}`} container alignItems="center">
          <CancelIcon style={{ color: '#BF4140', cursor: 'pointer' }} onClick={() => removeFile(i)} />
          {file.type.indexOf('image/') !== 0 ? (
            <InsertDriveFileOutlinedIcon fontSize="large" />
          ) : (
            <UploadedImage src={window.URL.createObjectURL(file)} />
          )}
          <span>{file.name}</span>
        </Grid>
      )),
    ],
    [files, initialFiles],
  );

  return (
    <>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <CommentLabel>
              コメント<RedText>必須</RedText>
            </CommentLabel>
            <CommentField variant={`outlined`} multiline minRows={5} value={newComment} onChange={onChange} />
            <WordCounter>{wordCount}/4000</WordCounter>
          </Grid>
        </Grid>
      </Grid>
      <Grid container item alignItems="center">
        {/* @ts-ignore: ts(2322) component prop does accept html tag names */}
        <SecondaryButton component="label">
          アップロードする素材・資料を選択する
          <input hidden id="attachment" name="attachment" type="file" accept="*/*" multiple onChange={onDrop} />
        </SecondaryButton>
        <UploadLabel>最大50MB / 最大3ファイル</UploadLabel>
      </Grid>
      <Grid item xs={12}>
        <BodySmall>
          資料や制作素材などのデータを添付ファイルとして追加できます。
          <br />
          最大3ファイルまでアップロードいただけます
        </BodySmall>
      </Grid>
      {SelectedImages}
      <Grid container justifyContent="flex-end">
        {SecondaryAction && { SecondaryAction }}
        {_PrimaryAction ? (
          PrimaryAction
        ) : (
          <PrimaryButton disabled={!newComment || wordCount === 0} onClick={onClickPrimary}>
            OK
          </PrimaryButton>
        )}
      </Grid>
    </>
  );
};

const EditCommentDrawer: FC<{
  comment: Comment;
  refetch: () => void;
  isOpen: boolean;
  onClose: () => void;
}> = ({ comment, refetch, isOpen, onClose }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [updateComment] = useMutation<PutCommentMutation, PutCommentMutationVariables>(PutCommentDocument, {
    context: { clientName: 'planbcd' },
  });

  const [postAttachements] = useMutation<PostAttachmentMutation, PostAttachmentMutationVariables>(
    PostAttachmentDocument,
    { context: { clientName: 'planbcd' } },
  );

  const onClickSave = (newComment: string, files: File[]) => {
    if (!newComment) {
      return globalState.setErrors({
        message: 'コメントを入力してください',
        name: 'NonFatalError',
        context: 'planbcd',
      });
    }
    setIsProcessing(true);
    updateComment(
      mutationVariables({
        id: comment.id,
        variation_comment: {
          comment: newComment,
        },
      }),
    )
      .then(({ data }) => {
        if (files.length) {
          const { putComment: putCommentData } = data as PutCommentMutation;
          Promise.all(
            files.map((file) => {
              return postAttachements(
                mutationVariables({
                  id: putCommentData.id,
                  data: file,
                }),
              );
            }),
          ).finally(() => {
            refetch();
            setIsProcessing(false);
          });
        } else {
          refetch();
          setIsProcessing(false);
        }
      })
      .catch((error) => {
        globalState.setErrors({
          message: `コメントの保存ができませんでした: (${error.message})`,
          name: 'NonFatalError',
          context: 'planbcd',
        });
        setIsProcessing(false);
      });
  };

  const initialFiles = comment.attachables_attachments?.map((a) => a.attachment);

  return (
    <Drawer anchor="right" open={isOpen} onClose={onClose}>
      <DrawerContainer>
        <DrawerContent>
          <CommentEditor
            callbackForOnClickPrimary={onClickSave}
            initialComment={comment.comment}
            initialFiles={initialFiles}
            _PrimaryAction={<PrimaryButton>変更を保存</PrimaryButton>}
          />
        </DrawerContent>
        {isProcessing && <LinearProgress />}
      </DrawerContainer>
    </Drawer>
  );
};

const DeleteCommentModal: FC<{
  commentId: string;
  refetch: () => void;
  isOpen: boolean;
  onClose: () => void;
}> = ({ commentId, refetch, isOpen, onClose }) => {
  const [deleteComment] = useMutation<DeleteCommentMutation, DeleteCommentMutationVariables>(DeleteCommentDocument, {
    context: { clientName: 'planbcd' },
  });
  const onClickOK = () => {
    deleteComment(
      mutationVariables({
        id: commentId,
      }),
    )
      .then(() => {
        globalState.setErrors({
          message: 'コメントを削除しました。',
          name: 'NonFatalError',
          context: 'サイト改善',
        });
        refetch();
      })
      .catch((e) => {
        console.log(e);
        globalState.setErrors({
          message: 'コメントを削除できませんでした。',
          name: 'NonFatalError',
          context: 'サイト改善',
        });
      })
      .finally(() => {
        onClose();
      });
  };
  return (
    <Dialog open={isOpen} onClose={onClose}>
      <ModalContent>
        <ModalTitle>コメントの削除</ModalTitle>
      </ModalContent>
      <ModalContent>
        <BodySmall>コメントを削除します。</BodySmall>
        <BodySmall>
          コメントを削除するともとに<span style={{ color: '#BF4140' }}>戻すことが出来ません</span>。
        </BodySmall>
        <BodySmall>よろしければ次にお進みください</BodySmall>
      </ModalContent>
      <ModalFooter>
        <SecondaryButton onClick={onClose}>キャンセル</SecondaryButton>
        <PrimaryButton onClick={onClickOK} style={{ backgroundColor: '#BF4140' }}>
          OK
        </PrimaryButton>
      </ModalFooter>
    </Dialog>
  );
};

// const ModalDeleteFile: FC<{
//   attachmentId: string;
//   refetch: () => void;
//   isOpen: boolean;
//   onClose: () => void;
// }> = ({ attachmentId, refetch, isOpen, onClose }) => {
//   const [deleteAttachablesAttachements] = useMutation<
//     DeleteAttachablesAttachmentsMutation,
//     DeleteAttachablesAttachmentsMutationVariables
//   >(DeleteAttachablesAttachmentsDocument, {
//     context: { clientName: 'planbcd' },
//   });

//   const onClickOK = () => {
//     deleteAttachablesAttachements(
//       mutationVariables({
//         id: attachmentId,
//       }),
//     ).finally(() => {
//       onClose();
//     });
//   };
//   return (
//     <Dialog open={isOpen} onClose={onClose}>
//       <ModalContent>
//         <ModalTitle>ファイルの削除</ModalTitle>
//       </ModalContent>
//       <ModalContent>
//         <BodySmall>ファイルを削除します。</BodySmall>
//         <BodySmall>
//           ファイルを削除するともとに<span style={{ color: '#BF4140' }}>戻すことが出来ません</span>。
//         </BodySmall>
//         <BodySmall>よろしければ次にお進みください</BodySmall>
//       </ModalContent>
//       <ModalFooter>
//         <SecondaryButton onClick={onClose}>キャンセル</SecondaryButton>
//         <PrimaryButton onClick={onClickOK} style={{ backgroundColor: '#BF4140' }}>
//           OK
//         </PrimaryButton>
//       </ModalFooter>
//     </Dialog>
//   );
// };

const SectionTitle = styled(_SectionTitle)(`
  margin-bottom: 1rem;
`);

const NameAndDate = styled(BodySmall)(`
  flex: 1;
  font-size: 12px;
`);

const ColumnFlexWrapper = styled('div')(`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  gap: 1rem;
  margin-top: 1rem;
  padding-bottom: 4rem;
`);

const CommentField = styled(TextField)(`
  width: 100%;
  border-radius: 8px;
  '& .MuiOutlinedInput-root': {
    color: ${COLOR_TEXT_MAIN};
    border-radius: 8px;
    font-size: 14px;
    height: 118px;
    '& fieldset': {
      outline: none;
      border: 1px solid #E6E7E8;
    };
    '&:hover fieldset': {
      outline: none;
      border: 1.5px solid #E6E7E8;
    };
    '&.Mui-focused fieldset': {
      outline: none;
      border: 1.5px solid #707889;
    };
    '&.Mui-error fieldset': {
      border: 1.5px solid #FE7877;
    };
    '&.Mui-error input': {
      color: #FE7877;
    };
  };
  '&.MuiOutlinedInput-notchedOutline': {
    fieldset: {
      outline: none;
      border: 1.5px solid #707889;
    };
  };
`);

const WordCounter = styled('span')(`
  font-size: 10px;
  margin-top: 6px;
  float: right;
`);

const UploadedImage = styled('img')(`
  max-width: 100%; 
  max-height: 120px;
  margin: 1rem;
`);

const RedText = styled('span')(`
  color: #BF4140;
  padding-left: 4px;
`);

const CommentLabel = styled(Typography)(`
  font-size: 12px;
  color: ${COLOR_TEXT_MAIN};
  margin: 1rem 0 0.4rem;
`);

const UploadLabel = styled('p')({
  ...LabelExtraSmallStyle,
  marginLeft: `${SPACING_SMALL}px`,
});

const EmptyFlexItem = styled(Grid)(`
  flex: 0 0 56px;
`);

const Link = styled(_Link)(`margin-right: 1rem`);

const Avatar = styled(_Avatar)(`
  margin-right: 1rem;
`);

const MoreHorizIcon = styled(_MoreHorizIcon)(`
  cursor: pointer;
  color: ${COLOR_TEXT_MAIN};
`);
const CreateIcon = styled(_CreateIcon)(`
  color: ${COLOR_TEXT_MAIN};
  margin-right: 0.4rem;
`);
const DeleteIcon = styled(_DeleteIcon)(`
  color: ${COLOR_RED};
  margin-right: 0.4rem;
`);

const AttachmentRow = styled('div')(`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 1rem;
`);

const ImageLink = styled(_Link)(`
  max-width: 20rem;
  flex: 0 1 auto;
`);

const FileOutlinedIcon = styled(InsertDriveFileOutlinedIcon)(`
  color: #4C566C;
  margin: 1rem;
`);

export default Comments;
